import { FindingsLabel } from '@app/icare/components/findings'
import { PatientAddressEditDrawer } from '@app/icare/entities/patient/edits/PatientAddressEditDrawer'
import { PatientRelativeEditDrawer } from '@app/icare/entities/patient/edits/PatientRelativeEditDrawer'
import { selectByFindingId, selectByGroup } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema'
import type { PatientAddressRelation } from '@app/icare/entities/patient/schema/PatientAddress'
import type { PatientRelativeRelation } from '@app/icare/entities/patient/schema/PatientRelative'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { AddButton } from '@pkg/ui/button'
import { DrawerSaveDropdown } from '@pkg/ui/drawer'
import { useRouterTabs } from '@pkg/ui/hooks/useRouterTabs'
import { useExtra, useTabs } from '@pkg/ui/layout/PageContainer'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useCallback } from 'react'
import { PatientAddressAlerts } from '../-components/PatientAddressAlerts'
import { useRelationCommitNavigation } from './-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/stammdaten')({
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
})

function RouteComponent() {
  const { id } = Route.useParams()
  const tabs = useRouterTabs({ basePath: `/patienten/${id}/stammdaten` })

  useTabs(
    {
      ...tabs,
      items: [
        {
          key: '/',
          label: <FindingsLabel label={t`Personendaten`} selector={selectByGroup('personal')} />,
        },
        {
          key: '/lieferadressen',
          label: (
            <FindingsLabel label={t`Lieferadressen`} selector={selectByFindingId('AddressesDeliveryIncomplete')} />
          ),
        },
        {
          key: '/rechnungsadressen',
          label: (
            <FindingsLabel label={t`Rechnungsadressen`} selector={selectByFindingId('AddressesBillingIncomplete')} />
          ),
        },
        {
          key: '/angehoerige',
          label: <FindingsLabel label={t`Angehörige`} selector={selectByGroup('relatives')} />,
        },
      ],
    },
    [tabs.activeKey],
  )

  const navigate = Route.useNavigate()

  const isInactiveRelation = useCallback(
    (relation: PatientAddressRelation | PatientRelativeRelation) => !isActivePatientRelation(relation),
    [],
  )
  const navigateOnAddressChange = useCallback(
    (relation: PatientAddressRelation) =>
      navigate({
        to: `/patienten/$id/stammdaten/${relation.type === 'delivery' ? 'lieferadressen' : 'rechnungsadressen'}`,
        params: { id },
        ignoreBlocker: true,
      }),
    [id, navigate],
  )
  const navigateOnRelativeChange = useCallback(
    async () =>
      await navigate({
        to: '/patienten/$id/stammdaten/angehoerige',
        params: { id },
        ignoreBlocker: true,
      }),
    [id, navigate],
  )

  const handleAfterCommitAddresses = useRelationCommitNavigation<PatientAddressRelation>({
    isInactive: isInactiveRelation,
    onCreateNavigate: navigateOnAddressChange,
    onInactiveEditNavigate: navigateOnAddressChange,
  })

  const handleAfterCommitRelatives = useRelationCommitNavigation<PatientRelativeRelation>({
    isInactive: isInactiveRelation,
    onCreateNavigate: navigateOnRelativeChange,
    onInactiveEditNavigate: navigateOnRelativeChange,
  })

  useExtra(
    <DrawerSaveDropdown
      items={[
        {
          key: 'add-delivery-address',
          label: t`Lieferadresse`,
          onClick: () =>
            PatientAddressEditDrawer.show({
              patientId: id,
              addressType: 'delivery',
              onAfterCommit: handleAfterCommitAddresses,
            }),
        },
        {
          key: 'add-billing-address',
          label: t`Rechnungsadresse`,
          onClick: () =>
            PatientAddressEditDrawer.show({
              patientId: id,
              addressType: 'billing',
              onAfterCommit: handleAfterCommitAddresses,
            }),
        },
        {
          key: 'add-relative',
          label: t`Angehörige(r)`,
          onClick: () => PatientRelativeEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommitRelatives }),
        },
      ]}
    >
      <AddButton>
        <Trans>Stammdaten hinzufügen</Trans>
      </AddButton>
    </DrawerSaveDropdown>,
    [id, handleAfterCommitAddresses, handleAfterCommitRelatives],
  )
  return (
    <>
      <PatientAddressAlerts patientId={id} />
      <Outlet />
    </>
  )
}
